import { FieldAssociations } from '@kitware/vtk.js/Common/DataModel/DataSet/Constants.js';
import vtkOpenGLHardwareSelector from '@kitware/vtk.js/Rendering/OpenGL/HardwareSelector';
import { forwardRef, useContext, useCallback, useMemo, useImperativeHandle } from 'react';
import deletionRegistry from '../utils/DeletionRegistry.js';
import useDebounce from '../utils/useDebounce.js';
import useGetterRef from '../utils/useGetterRef.js';
import useMount from '../utils/useMount.js';
import useUnmount from '../utils/useUnmount.js';
import { OpenGLRenderWindowContext, RendererContext, ViewContext } from './contexts.js';
import { useViewEventListener } from './modules/useViewEvents.js';

function useOpenGLHardwareSelector() {
    const [selectorRef, getSelector] = useGetterRef(() => {
        const selector = vtkOpenGLHardwareSelector.newInstance({
            captureZValues: true,
        });
        deletionRegistry.register(selector, () => selector.delete());
        return selector;
    });
    useUnmount(() => {
        if (selectorRef.current) {
            deletionRegistry.markForDeletion(selectorRef.current);
            selectorRef.current = null;
        }
    });
    return getSelector;
}
const DefaultProps = {
    enabled: true,
    pointerSize: 0,
    onHoverDebounceWait: 4,
};
var Picking = forwardRef(function ViewPicking(props, fwdRef) {
    const openGLRenderWindowAPI = useContext(OpenGLRenderWindowContext);
    const rendererAPI = useContext(RendererContext);
    const viewAPI = useContext(ViewContext);
    if (!openGLRenderWindowAPI || !rendererAPI || !viewAPI) {
        throw new Error('<Picking> must have a <View> ancestor');
    }
    const getSelector = useOpenGLHardwareSelector();
    useMount(() => {
        const selector = getSelector();
        selector.setFieldAssociation(FieldAssociations.FIELD_ASSOCIATION_POINTS);
        selector.attach(openGLRenderWindowAPI.get(), rendererAPI.get());
        return () => {
            // avoids keeping refs to the openglrenderwindow and renderer
            selector.attach(null, null);
        };
    });
    // --- Props --- //
    const { enabled = DefaultProps.enabled, pointerSize = DefaultProps.pointerSize, } = props;
    const getPointerSizeTolerance = useCallback(() => pointerSize / 2, [pointerSize]);
    // --- API --- //
    const pickClosest = useCallback((xp, yp, tolerance) => {
        const x1 = Math.floor(xp - tolerance);
        const y1 = Math.floor(yp - tolerance);
        const x2 = Math.ceil(xp + tolerance);
        const y2 = Math.ceil(yp + tolerance);
        const selector = getSelector();
        const openGLRenderWindow = openGLRenderWindowAPI.get();
        const renderer = rendererAPI.get();
        selector.setArea(x1, y1, x2, y2);
        if (!selector.captureBuffers()) {
            return [];
        }
        const pos = [xp, yp];
        const outSelectedPosition = [0, 0];
        const info = selector.getPixelInformation(pos, tolerance, outSelectedPosition);
        if (info == null || info.prop == null)
            return [];
        const startPoint = openGLRenderWindow.displayToWorld(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2), 0, renderer);
        const endPoint = openGLRenderWindow.displayToWorld(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2), 1, renderer);
        const ray = [Array.from(startPoint), Array.from(endPoint)];
        const worldPosition = Array.from(openGLRenderWindow.displayToWorld(info.displayPosition[0], info.displayPosition[1], info.zValue, renderer));
        const displayPosition = [
            info.displayPosition[0],
            info.displayPosition[1],
            info.zValue,
        ];
        return [
            Object.assign(Object.assign({ worldPosition,
                displayPosition, compositeID: info.compositeID }, info.prop.get('representationId')), { ray }),
        ];
    }, [rendererAPI, openGLRenderWindowAPI, getSelector]);
    const pick = useCallback((x1, y1, x2, y2) => {
        const selector = getSelector();
        const openGLRenderWindow = openGLRenderWindowAPI.get();
        const renderer = rendererAPI.get();
        selector.setArea(x1, y1, x2, y2);
        if (!selector.captureBuffers()) {
            return [];
        }
        const ray = [
            Array.from(openGLRenderWindow.displayToWorld(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2), 0, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2), 1, renderer)),
        ];
        const selections = selector.generateSelection(x1, y1, x2, y2) || [];
        return selections
            .map((v) => {
            const { prop, compositeID, displayPosition } = v.getProperties();
            // Return false to mark this item for removal
            if (prop == null || !displayPosition)
                return false;
            return Object.assign(Object.assign({ worldPosition: Array.from(openGLRenderWindow.displayToWorld(displayPosition[0], displayPosition[1], displayPosition[2], renderer)), displayPosition,
                compositeID }, prop.get('representationId')), { ray });
        })
            .filter(Boolean);
    }, [rendererAPI, openGLRenderWindowAPI, getSelector]);
    const pickWithFrustum = useCallback((x1, y1, x2, y2) => {
        const selector = getSelector();
        const openGLRenderWindow = openGLRenderWindowAPI.get();
        const renderer = rendererAPI.get();
        selector.setArea(x1, y1, x2, y2);
        if (!selector.captureBuffers()) {
            return null;
        }
        const frustum = [
            Array.from(openGLRenderWindow.displayToWorld(x1, y1, 0, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x2, y1, 0, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x2, y2, 0, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x1, y2, 0, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x1, y1, 1, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x2, y1, 1, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x2, y2, 1, renderer)),
            Array.from(openGLRenderWindow.displayToWorld(x1, y2, 1, renderer)),
        ];
        const representationIds = [];
        const selections = selector.generateSelection(x1, y1, x2, y2) || [];
        selections.forEach((v) => {
            const { prop } = v.getProperties();
            const getterResult = prop === null || prop === void 0 ? void 0 : prop.get('representationId');
            const representationId = getterResult === null || getterResult === void 0 ? void 0 : getterResult.representationId;
            if (representationId) {
                representationIds.push(representationId);
            }
        });
        return { frustum, representationIds };
    }, [rendererAPI, openGLRenderWindowAPI, getSelector]);
    const api = useMemo(() => ({
        pick,
        pickWithFrustum,
        pickClosest,
    }), [pick, pickWithFrustum, pickClosest]);
    useImperativeHandle(fwdRef, () => api);
    // --- Pointer event handling --- //
    const getScreenEventPositionFor = useCallback((source) => {
        const rw = openGLRenderWindowAPI.get();
        const canvas = rw.getCanvas();
        if (!canvas)
            return { x: 0, y: 0, z: 0 };
        const bounds = canvas.getBoundingClientRect();
        const [canvasWidth, canvasHeight] = rw.getSize();
        const scaleX = canvasWidth / bounds.width;
        const scaleY = canvasHeight / bounds.height;
        const position = {
            x: scaleX * (source.clientX - bounds.left),
            y: scaleY * (bounds.height - source.clientY + bounds.top),
            z: 0,
        };
        return position;
    }, [openGLRenderWindowAPI]);
    const { onHover, onPointerDown, onPointerUp, onClick } = props;
    const makeSelection = useCallback((ev) => {
        const { x, y } = getScreenEventPositionFor(ev);
        const tolerance = getPointerSizeTolerance();
        const selections = pickClosest(Math.floor(x), Math.floor(y), tolerance);
        return selections[0];
    }, [getScreenEventPositionFor, getPointerSizeTolerance, pickClosest]);
    // --- register event handlers --- //
    const { onHoverDebounceWait = DefaultProps.onHoverDebounceWait } = props;
    // TODO last selection? (see View.js)
    useViewEventListener('pointermove', useDebounce(useCallback((ev) => {
        if (!enabled || !onHover)
            return;
        onHover(makeSelection(ev), ev);
    }, [makeSelection, onHover, enabled]), onHoverDebounceWait));
    useViewEventListener('pointerdown', useCallback((ev) => {
        if (!enabled || !onPointerDown)
            return;
        onPointerDown(makeSelection(ev), ev);
    }, [makeSelection, onPointerDown, enabled]));
    useViewEventListener('pointerup', useCallback((ev) => {
        if (!enabled || !onPointerUp)
            return;
        onPointerUp(makeSelection(ev), ev);
    }, [makeSelection, onPointerUp, enabled]));
    useViewEventListener('click', useCallback((ev) => {
        if (!enabled || !onClick)
            return;
        onClick(makeSelection(ev), ev);
    }, [makeSelection, onClick, enabled]));
    return null;
});

export { Picking as default };
